import { AssetType } from "common/constants";
import { getLogoPath, isThemeBackgroundAsset, isThemeBackgroundVideo } from "common/assetUtils";
import { ds } from "js/core/models/dataService";
import Logos from "js/core/models/logos";

export async function getThemeBackgroundImageUrl(backgroundImage, { size = "original", reloadCache = false } = {}) {
    if (!backgroundImage?.asset) {
        return null;
    }

    if (isThemeBackgroundVideo(backgroundImage)) {
        // Videos can't be shown in an <img>/<image> tag, so everything that needs a static
        // representation of the background (previews, thumbnails, exports) uses the still frame
        return getThemeBackgroundVideoPreviewUrl(backgroundImage, { size, reloadCache });
    }

    if (isThemeBackgroundAsset(backgroundImage)) {
        const asset = await ds.assets.getAssetById(backgroundImage.asset, backgroundImage.content_type);
        return asset.getURL(size, reloadCache);
    }

    return Logos.getSignedUrlAndLoad(getLogoPath(backgroundImage.asset), reloadCache);
}

export async function getThemeBackgroundVideoPreviewUrl(backgroundVideo, { size = "original", reloadCache = false } = {}) {
    // Stock videos carry their preview url with them, uploads have a generated preview asset
    if (backgroundVideo.previewUrl) {
        return backgroundVideo.previewUrl;
    }

    const videoAsset = await ds.assets.getAssetById(backgroundVideo.asset, backgroundVideo.content_type);
    const previewAssetId = videoAsset.get("previewAssetId");
    if (!previewAssetId) {
        return null;
    }

    const previewAsset = await ds.assets.getAssetById(previewAssetId, AssetType.IMAGE);
    return previewAsset.getURL(size, reloadCache);
}

/**
 * Theme background videos are stored on the theme (keyed by id like background images are),
 * this turns one into the media element model the canvas content element expects.
 */
export function getThemeBackgroundVideoModel(backgroundVideo) {
    return {
        content_type: backgroundVideo.content_type,
        content_value: backgroundVideo.asset,
        content_url: backgroundVideo.content_url ?? null,
        previewUrl: backgroundVideo.previewUrl ?? null,
        assetProps: {
            ...backgroundVideo.assetProps,
            // Background videos always play silently on a loop and are never interactive
            autoPlay: true,
            controls: false,
            loop: true,
            muted: true
        }
    };
}
